import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import { useDelayedEvent } from '@cast/utils';

import { DrawerOverlay, DrawerOverlayProps } from './DrawerOverlay';
import { useDrawerInstance } from './hooks';
import { Scroller } from '../scroller';

export type ShowOverlayArgs = {
  id?: string;
  content: ReactNode;
  props?: DrawerOverlayProps;
  duration?: number;
  onDestroy?: () => void;
  closeOnDestroy?: boolean;
};

export type DrawerOverlayState = {
  showOverlay: (args: ShowOverlayArgs) => void;
  isOverlayActive: boolean;
  activeOverlayId?: string;
  destroyOverlay: () => void;
};

const Context = createContext<DrawerOverlayState>({
  showOverlay: () =>
    console.error(
      "Calling 'showOverlay' when content is not wrapped with DrawerOverlayProvider"
    ),
  isOverlayActive: false,
  destroyOverlay: () =>
    console.error(
      "Calling 'destroyOverlay' when content is not wrapped with DrawerOverlayProvider"
    ),
});

type Props = {
  children?: ReactNode | ((state: DrawerOverlayState) => ReactNode);
  overlayProps?: DrawerOverlayProps;
  /** @deprecated use `DrawerBody` from `@cast/design-system` instead to enable scrollable content */
  wrapWithScroller?: boolean;
};

export const DrawerOverlayProvider = ({
  children,
  overlayProps,
  wrapWithScroller,
}: Props) => {
  const { close } = useDrawerInstance();
  const [activeOverlayId, setActiveOverlayId] = useState<string | undefined>();
  const [activeOverlayProps, setActiveOverlayProps] = useState<
    DrawerOverlayProps | undefined
  >();
  const [overlayContent, setOverlayContent] = useState<ReactNode>(null);
  const isOverlayActive = !!overlayContent;

  const delayedEvent = useDelayedEvent();

  const destroyOverlay = useCallback(() => {
    setOverlayContent(null);
    setActiveOverlayProps(undefined);
    setActiveOverlayId(undefined);
    if (delayedEvent.isActive) {
      delayedEvent.clear();
    }
  }, [setOverlayContent, delayedEvent]);

  const showOverlay = useCallback(
    ({
      content,
      props,
      duration,
      onDestroy,
      closeOnDestroy,
      id,
    }: ShowOverlayArgs) => {
      setOverlayContent(content);
      setActiveOverlayProps(props);
      setActiveOverlayId(id);
      if (duration) {
        delayedEvent.activate({
          milliseconds: duration,
          onEnded: () => {
            onDestroy?.();
            if (closeOnDestroy) {
              close();
            } else {
              destroyOverlay();
            }
          },
        });
      } else {
        setOverlayContent(content);
      }
    },
    [setOverlayContent, delayedEvent, close, destroyOverlay]
  );

  const content =
    typeof children === 'function' ? (
      <Context.Consumer>{children}</Context.Consumer>
    ) : (
      children
    );

  return (
    <Context.Provider
      value={{ showOverlay, isOverlayActive, destroyOverlay, activeOverlayId }}
    >
      {wrapWithScroller ? (
        <Scroller sx={{ height: '100vh' }}>{content}</Scroller>
      ) : (
        content
      )}
      {isOverlayActive && (
        <DrawerOverlay {...overlayProps} {...activeOverlayProps}>
          {overlayContent}
        </DrawerOverlay>
      )}
    </Context.Provider>
  );
};

export const useDrawerOverlay = () => useContext(Context);
